import { H5 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { memo } from 'react';
import isEqual from 'react-fast-compare';

import { BuyersPremium } from '@/components/BuyersPremium/BuyersPremium';
import { useAppSelector } from '@/redux/hooks';
import { getItemSummaryCatalogId, getItemSummaryStartTime } from '@/redux/modules/item/summary/itemSummary.selectors';
import {
    getItemDetailConditionReport,
    getItemDetailDescription,
    getItemDetailDimensions,
    getItemDetailWeight,
} from '@/redux/modules/itemDetail';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { safelyRender } from '@/utils/safelyRender';

import { DescriptionSection } from './DescriptionSection';

const FIVE_YEARS_AGO = (Date.now() - 1.577e11) / 1000; // 5 YEARS!

type Props = {
    className?: string;
    itemId: number;
};

const ItemDescriptionComponent = ({ className = '', itemId }: Props) => {
    const formatMessage = useAppFormatMessage();
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const saleStartTs = useAppSelector((state) => getItemSummaryStartTime(state, itemId));
    const conditionReport = useAppSelector((state) => getItemDetailConditionReport(state, itemId));
    const description = useAppSelector((state) => getItemDetailDescription(state, itemId));
    const dimensions = useAppSelector((state) => getItemDetailDimensions(state, itemId));
    const weight = useAppSelector((state) => getItemDetailWeight(state, itemId));

    const showDimensions = dimensions.trim() !== '';
    const showWeight = weight.trim() !== '';

    const old = saleStartTs < FIVE_YEARS_AGO;

    return (
        <div
            className={cn(
                'relative mt-lg overflow-hidden border-t border-border-secondary pt-lg print:flex print:flex-col [&>*]:print:mx-auto [&>*]:print:my-[12px] [&>*]:print:max-w-[400px]',
                className
            )}
        >
            <H5 className="mb-sm xsMax:mt-0">
                {formatMessage(['bidding_console', 'console', 'currentLiveItem', 'headers', 'itemDetails'])}
            </H5>
            <DescriptionSection
                body={safelyRender({
                    fontStyle: 1,
                    html: description,
                    old: old,
                    wrapper: 'div',
                })}
                header="bidding_console.console.currentLiveItem.headers.description"
            />
            {conditionReport && (
                <DescriptionSection
                    body={safelyRender({
                        fontStyle: 1,
                        html: conditionReport,
                        wrapper: 'div',
                    })}
                    header="bidding_console.console.currentLiveItem.headers.condition"
                />
            )}
            {showDimensions && (
                <DescriptionSection
                    body={safelyRender({
                        fontStyle: 1,
                        html: dimensions,
                        wrapper: 'div',
                    })}
                    header="bidding_console.console.currentLiveItem.headers.dimensions"
                />
            )}
            {showWeight && (
                <DescriptionSection
                    body={safelyRender({
                        fontStyle: 1,
                        html: weight,
                        wrapper: 'div',
                    })}
                    header="bidding_console.console.currentLiveItem.headers.weight"
                />
            )}
            <BuyersPremium catalogId={catalogId} />
        </div>
    );
};

export const ItemDescription = memo<Props>(ItemDescriptionComponent, isEqual);
